const MD_BREAKPOINT = 744;

export default function initInpageNav() {
  const inpageNav = document.querySelector(".inpage-nav");
  if (!inpageNav) return;

  const navbarWrapper = document.querySelector(".navbar-wrapper");
  const mainElement = document.querySelector("main");
  const navLinks = inpageNav.querySelectorAll("ul li a");
  const navInner = inpageNav.querySelector(".inpage-nav-inner");

  if (!navLinks.length) return;

  // Create placeholder to track original position
  const placeholder = document.createElement("div");
  placeholder.className = "inpage-nav-placeholder";
  placeholder.style.cssText = "height: 0; visibility: hidden; pointer-events: none;";
  inpageNav.parentNode.insertBefore(placeholder, inpageNav);

  // Create mobile dropdown
  createMobileDropdown();

  // Calculate navbar height
  function getNavbarHeight() {
    if (!navbarWrapper) return 0;
    const navbarContainer = navbarWrapper.querySelector(".navbar-container");
    return navbarContainer ? navbarContainer.offsetHeight : 0;
  }

  // Check if navbar is hidden (scrolled down on mobile/tablet)
  function isNavbarHidden() {
    if (!navbarWrapper) return false;
    return (
      navbarWrapper.classList.contains("scrolled") &&
      navbarWrapper.classList.contains("scroll-down")
    );
  }

  // Get offset for scroll calculations
  function getScrollOffset() {
    let offset = inpageNav.offsetHeight;
    if (!isNavbarHidden()) {
      offset += getNavbarHeight();
      // Add navbar wrapper padding
      if (window.innerWidth >= MD_BREAKPOINT) {
        offset += 16; // spacing-sm * 2
      } else {
        offset += 8; // spacing-xs * 2
      }
    }
    return offset + 20; // Extra buffer
  }

  // Update CSS variable for navbar height
  function updateNavbarHeightVar() {
    const height = getNavbarHeight();
    document.documentElement.style.setProperty("--navbar-height", `${height}px`);
  }

  // Create mobile dropdown structure
  function createMobileDropdown() {
    const dropdown = document.createElement("div");
    dropdown.className = "inpage-nav-dropdown";

    // Get first link text as default
    const firstLink = navLinks[0];
    const defaultText = firstLink ? firstLink.textContent : "Select";

    dropdown.innerHTML = `
      <button type="button" class="inpage-nav-dropdown-toggle">
        <span>${defaultText}</span>
        <svg class="icon-chevron" width="24" height="24">
          <use href="/_resources/themes/landcom/assets/src/assets/icons.svg#icon-chevron-down"></use>
        </svg>
        <svg class="icon-close" width="24" height="24">
          <use href="/_resources/themes/landcom/assets/src/assets/icons.svg#icon-close"></use>
        </svg>
      </button>
      <div class="inpage-nav-dropdown-menu"></div>
    `;

    const menu = dropdown.querySelector(".inpage-nav-dropdown-menu");

    // Clone links to dropdown
    navLinks.forEach((link) => {
      const dropdownLink = document.createElement("a");
      dropdownLink.href = link.getAttribute("href");
      dropdownLink.textContent = link.textContent;
      if (link.classList.contains("active")) {
        dropdownLink.classList.add("active");
      }
      menu.appendChild(dropdownLink);
    });

    navInner.appendChild(dropdown);

    // Toggle dropdown
    const toggle = dropdown.querySelector(".inpage-nav-dropdown-toggle");
    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      dropdown.classList.toggle("is-open");
      navInner.classList.toggle("dropdown-open");
    });

    // Close on outside click
    document.addEventListener("click", (e) => {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove("is-open");
        navInner.classList.remove("dropdown-open");
      }
    });

    // Handle dropdown link clicks
    const dropdownLinks = menu.querySelectorAll("a");
    dropdownLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        dropdown.classList.remove("is-open");
        navInner.classList.remove("dropdown-open");

        const targetId = link.getAttribute("href");
        scrollToSection(targetId);
      });
    });
  }

  // Scroll to section with offset
  function scrollToSection(targetId) {
    if (!targetId || targetId === "#") return;

    const targetElement = document.querySelector(targetId);
    if (!targetElement) return;

    const offset = getScrollOffset();
    const targetPosition =
      targetElement.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: targetPosition,
      behavior: "smooth",
    });
  }

  // Update active state based on scroll position
  function updateActiveState() {
    const scrollPosition = window.scrollY + getScrollOffset() + 50;

    let activeLink = null;

    navLinks.forEach((link) => {
      const targetId = link.getAttribute("href");
      if (!targetId || targetId === "#") return;

      const targetElement = document.querySelector(targetId);
      if (!targetElement) return;

      const sectionTop = targetElement.offsetTop;
      const sectionBottom = sectionTop + targetElement.offsetHeight;

      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        activeLink = link;
      }
    });

    // Update desktop links
    navLinks.forEach((link) => link.classList.remove("active"));
    if (activeLink) {
      activeLink.classList.add("active");
    }

    // Update mobile dropdown
    const dropdown = inpageNav.querySelector(".inpage-nav-dropdown");
    if (dropdown) {
      const toggleText = dropdown.querySelector(
        ".inpage-nav-dropdown-toggle span"
      );
      const dropdownLinks = dropdown.querySelectorAll(
        ".inpage-nav-dropdown-menu a"
      );

      dropdownLinks.forEach((link) => link.classList.remove("active"));

      if (activeLink) {
        toggleText.textContent = activeLink.textContent;
        // Find matching dropdown link
        dropdownLinks.forEach((link) => {
          if (link.getAttribute("href") === activeLink.getAttribute("href")) {
            link.classList.add("active");
          }
        });
      }
    }
  }

  // Handle sticky behavior
  function handleScroll() {
    if (!mainElement) return;

    const navbarHeight = getNavbarHeight();
    const navbarPadding = window.innerWidth >= MD_BREAKPOINT ? 16 : 8;
    const stickyTrigger = navbarHeight + navbarPadding;

    // Use placeholder to get original position
    const placeholderRect = placeholder.getBoundingClientRect();
    const placeholderTop = placeholderRect.top;

    // Should become sticky when placeholder top reaches navbar bottom
    const shouldBeSticky = placeholderTop <= stickyTrigger;

    // Check if we're still within main element
    const mainRect = mainElement.getBoundingClientRect();
    const inpageNavHeight = inpageNav.offsetHeight;
    const isWithinMain = mainRect.bottom > inpageNavHeight + stickyTrigger;

    // Toggle sticky class
    if (shouldBeSticky && isWithinMain) {
      inpageNav.classList.add("is-sticky");
      inpageNav.classList.remove("is-hidden");
    } else if (!shouldBeSticky) {
      inpageNav.classList.remove("is-sticky");
      inpageNav.classList.remove("is-hidden");
    } else if (!isWithinMain) {
      inpageNav.classList.add("is-hidden");
    }

    // Update navbar hidden class for positioning
    if (isNavbarHidden()) {
      inpageNav.classList.add("navbar-hidden");
    } else {
      inpageNav.classList.remove("navbar-hidden");
    }

    // Update active state
    updateActiveState();
  }

  // Handle link clicks for smooth scrolling
  navLinks.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const targetId = link.getAttribute("href");
      scrollToSection(targetId);
    });
  });

  // Throttled scroll handler
  let ticking = false;
  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  }

  // Initialize
  updateNavbarHeightVar();

  window.addEventListener("scroll", onScroll);
  window.addEventListener("resize", () => {
    updateNavbarHeightVar();
    handleScroll();
  });

  // Initial call
  handleScroll();
}
